import type Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { createNativeTextInput } from "@/ui/NativeTextInput";
import { validateDisplayName } from "@/online/validation";

const PANEL_WIDTH = 420;
const PANEL_HEIGHT = 380;
const INPUT_WIDTH = 300;
const INPUT_HEIGHT = 46;
const NAME_MAX_LENGTH = 16;
/** Gap between errorText's own (measured) bottom edge and SAVE, and between SAVE and NOT NOW - shared by `relayout()` and `repositionButtonsForError()` so the two can never drift apart. */
const SAVE_BUTTON_GAP = 24;
const BUTTON_ROW_GAP = 58;

export interface NameEntryOpenOptions {
  /** Big top line - e.g. "NEW HIGH SCORE!" or "EDIT NAME". */
  headline: string;
  /** Optional line under the headline - e.g. "YOU'RE #3 ON OPEN FIELD". */
  contextLine?: string;
  initialValue?: string;
  /**
   * Whether the text input grabs focus (and, on mobile, pops the on-screen
   * keyboard) the instant this modal opens. Defaults to `true` - the
   * modal's own `open()` call being reached is already the result of an
   * explicit player action (an EDIT NAME/SAVE YOUR NAME button press), so
   * focusing immediately is the expected, wanted behavior there. Pass
   * `false` only when `open()` can be reached WITHOUT any such action -
   * e.g. an automatic post-death prompt - so the keyboard never pops up
   * unprompted; the input stays visible and tappable, and only a player's
   * own explicit tap on it (or a future explicit control) then focuses it.
   */
  autoFocus?: boolean;
}

export interface NameEntryModalHandle {
  container: Phaser.GameObjects.Container;
  open(options: NameEntryOpenOptions): void;
  setVisible(visible: boolean): void;
  relayout(): void;
  destroy(): void;
}

/**
 * Reused for both flows the spec calls out: the "you just hit a new
 * Top 10 result and have no name yet" prompt (from GameScene's
 * level-complete flow) and the "EDIT NAME" entry point (from the
 * Leaderboard screen). The only difference between them is what `open()`
 * is called with - the save/cancel wiring, validation, and the actual
 * text field are identical either way.
 */
export function createNameEntryModal(
  scene: Phaser.Scene,
  depth: number,
  handlers: { onSave: (name: string) => Promise<{ ok: boolean; error?: string }>; onNotNow: () => void },
): NameEntryModalHandle {
  const container = scene.add.container(0, 0).setDepth(depth).setVisible(false);

  const dim = scene.add.rectangle(0, 0, 10, 10, 0x0a0c12, 0.85);
  container.add(dim);

  const panelBg = scene.add.graphics();
  container.add(panelBg);

  const headlineText = scene.add
    .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "22px", fontStyle: "bold", color: "#7dffb3" })
    .setOrigin(0.5, 0);
  container.add(headlineText);

  const contextText = scene.add
    .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "14px", color: "#d7dbe6" })
    .setOrigin(0.5, 0);
  container.add(contextText);

  const promptText = scene.add
    .text(0, 0, "ENTER YOUR NAME", { fontFamily: "Arial, sans-serif", fontSize: "13px", fontStyle: "bold", color: "#8b93a7" })
    .setOrigin(0.5, 0);
  container.add(promptText);

  const inputBoxBg = scene.add.graphics();
  container.add(inputBoxBg);

  const publicHint = scene.add
    .text(0, 0, "Your name will be public.", { fontFamily: "Arial, sans-serif", fontSize: "12px", color: "#8b93a7" })
    .setOrigin(0.5, 0);
  container.add(publicHint);

  const errorText = scene.add
    .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "12px", color: "#ff5f6d", align: "center" })
    .setOrigin(0.5, 0);
  container.add(errorText);

  const saveButton = createButton(scene, 0, 0, "SAVE", () => void trySave(), { width: 180, height: 48 });
  container.add(saveButton);

  const notNowButton = createButton(scene, 0, 0, "NOT NOW", () => notNow(), {
    width: 180,
    height: 42,
    fillColor: 0x1c2030,
    hoverColor: 0x262c40,
    textColor: "#f5f7fa",
  });
  container.add(notNowButton);

  const input = createNativeTextInput({ maxLength: NAME_MAX_LENGTH, placeholder: "Your name" });
  let saving = false;
  let isOpen = false;

  function hideInput(): void {
    input.setVisible(false);
    // A real DOM element losing focus is what actually dismisses a
    // mobile virtual keyboard - there's no separate "close keyboard" API.
    input.blur();
  }

  function onWindowKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") notNow();
  }

  /**
   * The one place either dismissal path (SAVE-success and NOT NOW/Escape)
   * actually closes the modal - hides every element it owns and undoes
   * everything `open()` set up. Idempotent: a second call (e.g. NOT NOW
   * clicked twice, or firing after a save that already closed it) is a
   * harmless no-op, so nothing here needs its own duplicate-call guard.
   */
  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    saving = false;
    container.setVisible(false);
    hideInput();
    errorText.setText("");
    // Disabling input (not just hiding the container) means a stale
    // Phaser hit area can never intercept a pointer/touch meant for
    // whatever is now visible underneath - see Button.ts's setEnabled().
    // Both are re-enabled in open() for next time.
    saveButton.setEnabled(false);
    notNowButton.setEnabled(false);
    globalThis.window?.removeEventListener?.("keydown", onWindowKeyDown);
  }

  function notNow(): void {
    // Synchronous, no network wait - the player must always be able to
    // escape, including while a SAVE request is still in flight.
    close();
    handlers.onNotNow();
  }

  /**
   * `relayout()` positions SAVE/NOT NOW from `errorText`'s height at the
   * time it runs - which is only ever called on open/resize, while
   * `errorText` is still empty. Every later `errorText.setText(...)` (live
   * validation while typing, or an async save's server error, which can be
   * an arbitrary-length string that wraps to several lines) must
   * recompute those two positions from the text's NOW-current measured
   * height, or a multi-line error visually overlaps buttons left at their
   * stale, empty-text position. Deliberately lighter than calling the full
   * `relayout()` (which also redraws the panel and re-lays-out the native
   * DOM input) - this runs on every keystroke and must never flicker the
   * input or steal its focus.
   */
  function repositionButtonsForError(): void {
    const y = errorText.y + Math.max(18, errorText.height + 4);
    saveButton.setPosition(saveButton.x, y + SAVE_BUTTON_GAP);
    notNowButton.setPosition(notNowButton.x, y + SAVE_BUTTON_GAP + BUTTON_ROW_GAP);
  }

  function updateValidation(): void {
    const value = input.getValue();
    if (value.length === 0) {
      errorText.setText("");
      repositionButtonsForError();
      return;
    }
    const result = validateDisplayName(value);
    errorText.setText(result.valid ? "" : (result.error ?? ""));
    repositionButtonsForError();
  }
  input.onChange(updateValidation);
  input.onSubmit(() => void trySave());

  async function trySave(): Promise<void> {
    if (saving || !isOpen) return;
    const value = input.getValue();
    const validation = validateDisplayName(value);
    if (!validation.valid) {
      errorText.setText(validation.error ?? "Invalid name.");
      repositionButtonsForError();
      return;
    }
    saving = true;
    saveButton.setEnabled(false);
    errorText.setText("");
    repositionButtonsForError();
    let result: { ok: boolean; error?: string };
    try {
      result = await handlers.onSave(validation.normalized);
    } catch {
      result = { ok: false, error: "Could not save name. Please try again." };
    }
    // NOT NOW/Escape may have already closed the modal while this was
    // in flight - a late response must never reopen or re-touch it.
    if (!isOpen) return;
    saving = false;
    if (!result.ok) {
      saveButton.setEnabled(true);
      errorText.setText(result.error ?? "Could not save name. Please try again.");
      repositionButtonsForError();
      return;
    }
    close();
  }

  function relayout(): void {
    const { width, height } = scene.scale;
    dim.setSize(width, height).setPosition(width / 2, height / 2);

    const panelWidth = Math.min(PANEL_WIDTH, width * 0.92);
    const panelHeight = Math.min(PANEL_HEIGHT, height * 0.92);
    const panelX = width / 2 - panelWidth / 2;
    const panelY = height / 2 - panelHeight / 2;

    panelBg.clear();
    panelBg.fillStyle(0x171a24, 1);
    panelBg.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);
    panelBg.lineStyle(1.5, GameConfig.colors.gridLine, 1);
    panelBg.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);

    let y = panelY + 22;
    headlineText.setPosition(width / 2, y);
    y += 32;

    if (contextText.text.length > 0) {
      contextText.setPosition(width / 2, y);
      y += 24;
    }

    promptText.setPosition(width / 2, y);
    y += 26;

    const inputWidth = Math.min(INPUT_WIDTH, panelWidth - 64);
    const inputX = width / 2 - inputWidth / 2;
    inputBoxBg.clear();
    inputBoxBg.fillStyle(0x0a0c12, 1);
    inputBoxBg.fillRoundedRect(inputX, y, inputWidth, INPUT_HEIGHT, 10);
    inputBoxBg.lineStyle(1.5, GameConfig.colors.gridLine, 1);
    inputBoxBg.strokeRoundedRect(inputX, y, inputWidth, INPUT_HEIGHT, 10);
    input.layout(scene, inputX, y, inputWidth, INPUT_HEIGHT);
    y += INPUT_HEIGHT + 8;

    publicHint.setPosition(width / 2, y);
    y += 20;

    errorText.setWordWrapWidth(panelWidth - 48, true);
    errorText.setPosition(width / 2, y);
    y += Math.max(18, errorText.height + 4);

    saveButton.setPosition(width / 2, y + SAVE_BUTTON_GAP);
    notNowButton.setPosition(width / 2, y + SAVE_BUTTON_GAP + BUTTON_ROW_GAP);
  }

  relayout();
  // Starts fully non-interactive, matching the container's initial
  // setVisible(false) above - openInternal() turns both back on.
  saveButton.setEnabled(false);
  notNowButton.setEnabled(false);

  function openInternal(): void {
    isOpen = true;
    saving = false;
    container.setVisible(true);
    saveButton.setEnabled(true);
    notNowButton.setEnabled(true);
    relayout();
    input.setVisible(true);
    globalThis.window?.addEventListener?.("keydown", onWindowKeyDown);
  }

  return {
    container,
    open(options: NameEntryOpenOptions) {
      headlineText.setText(options.headline);
      contextText.setText(options.contextLine ?? "");
      input.setValue(options.initialValue ?? "");
      errorText.setText("");
      openInternal();
      if (options.autoFocus ?? true) input.focus();
    },
    setVisible(visible: boolean) {
      if (visible) openInternal();
      else close();
    },
    relayout,
    destroy() {
      close();
      input.destroy();
      container.destroy();
    },
  };
}
